import * as constants from './library_constants'
export type LibraryFolder = {
  uuid: string,
  name: string,
  collection_type?: string,
  creator?: string,
  owner?: string,
  owner_name?: string,
  product?: string,
  desc?: string,
  n?: number,
  share_token?: string,
  content_counts?: StringNumberPair,
  score?: number,
  created_at?: string,
  updated_at?: string,
  deleted_at?: string,
  archived_at?: string,
  image_url?: string,
  thumbnail_url?: string
  content?: LibraryContentObject[]
  folder_roles?: FolderUserRole[]
  contributors?: string[]
  permissions?: string[]
}

export type LibraryFolderResponse = {
  collection: LibraryFolder,
  permissions?: string[]
}

export type ErrorResponse = {
  status: string,
  status_code: number,
  message: string,
}
export type ContentListResponse = {
  content: LibraryContentObject[]
}

interface StringNumberPair {
  [key: string]: number;
}

interface StringStringArrayPair {
  [key: string]: string[]
}

interface StringBooleanPair {
  [key: string]: boolean;
}

interface StandardSetRef {
  standard_long_ref: string,
  standard_set_code: string,
}

export interface SubjectAlignment {
  domains: string[],
  subject: string
}

export type LibraryFolders = {
  collections: LibraryFolder[]
}

export type LibraryContentObject = {
  uuid: string,
  collection_uuid: string,
  content_uuid: string,
  content_type: string,
  content_title?: string,
  content_thumbnail_url?: string
  content_body_error?: string,
  text?: string,
  contributor?: string,
  created_at: string,
  updated_at?: string,
  deleted_at?: string,
  sequence?: number
  content_body: LibraryGoal | LibraryStrategy | LibraryResource | LibraryPresentLevel | LibraryPresentLevelStudentSummary | LibraryImpactOfDisability,
  show_folder_name?: boolean,
  collection_body?: LibraryFolder,
  event_time?: string
}

import { Goal } from '@/goals/goal_types'
export interface LibraryGoal extends Goal {
  _id: string;
  _type: string;
  access_grade_level_uuid?: string;
  access_point_level?: string;
  attention_goal?: string;
  created_at?: string;
  expressive_language_goal?: string;
  goal_resources?: string[];
  goal_uuid?: string;
  grade_level_goal: string;
  grades: string[];
  hearing_goal?: string,
  levels_personalized?: StringBooleanPair
  personalized?: boolean,
  physical_goal?: string,
  resource_languages?: string[],
  save_by_copy?: boolean,
  share_link?: string,
  shared?: boolean,
  standard_set_refs: StandardSetRef[],
  standard_sets?: string[],
  student_name?: string,
  student_uuid?: string,
  subject?: string,
  subject_alignments?: SubjectAlignment[],
  tags?: string[],
  updated_at?: string,
  updated_time?: number,
  user_content_profile_id: string,
  user_uuid: string,
  uuid: string,
  visual_goal?: string
}

export type LibraryStrategy = {
  _id: string,
  created_at?: string,
  favorite?: boolean,
  notes?: string,
  shared?: boolean,
  source_strategy: SourceStrategy,
  strategy_uuid: string,
  student_name?: string,
  student_uuid: string,
  uuid: string,
  updated_at?: string,
  updated_time?: number,
  user_uuid: string,
}

export type SourceStrategy = {
  _id: string,
  alt_names: string[],
  author_id: string,
  created_at?: string,
  demo: boolean,
  description: string,
  examples: { text: string }[]
  grades: string[],
  image_attribution?: string,
  image_url?: string,
  instructional_settings?: string[],
  published: boolean,
  related_adaptation_uuids: string[],
  resource_count: number,
  score: number,
  slug: string,
  tags: string[],
  teaching_types: string[],
  tips: { text: string }[],
  title: string,
  udl_standard_refs: string[],
  updated_at?: string,
  updated_time?: number,
  uuid: string,
}

export type LibraryPresentLevel = {
  _id: string,
  created_at?: string,
  drafted_needs?: string,
  drafted_strengths?: string,
  enrolled_grade_level?: string,
  final_needs?: string,
  final_strengths?: string,
  selected_subjects: string[],
  student_name: string,
  student_pronoun?: string,
  student_uuid: string,
  updated_at?: string,
  user_uuid?: string,
  uuid: string,
  resource_count?: number,
}

export type LibraryPresentLevelStudentSummary = {
  _id: string,
  created_at?: string,
  creator_uuid?: string,
  grade_label?: string,
  grade_uuid?: string,
  pl_session_uuid?: string,
  statement_text: string,
  student_name: string,
  subject_label: string,
  subject_uuid?: string,
  summarized_category_names?: string[],
  updated_at?: string,
  uuid?: string
}

export type ImpactArea = {
  area_label: string,
  area_title: string,
  area_order: string,
  area_statement_text: string,
  area_uuid: string,
  trial_access?: string,
  demo_access?: string,
  locked?: boolean
}

export type LibraryImpactOfDisability = {
  _id: string,
  created_at?: string,
  disability: string,
  impact_areas: ImpactArea[],
  statement: string,
  student_name?: string,
  student_uuid?: string,
  subjects: string[],
  updated_at?: string,
  updated_time?: number,
  user_uuid: string,
  uuid: string,
  shared?: boolean,
  share_link?: string
}

export type LibraryResource = {
  _id: string,
  alt_assessment?: boolean,
  created_at?: string,
  created_by?: string,
  demo: boolean,
  description: string,
  download_links: { label: string, url: string }[],
  estimated_time?: string,
  generated_images_by_url?: StringStringArrayPair
  google_drive_links: { label: string, link_type: string, url: string }[],
  grades: string[],
  is_website?: boolean,
  language?: string,
  num_pages?: number,
  project_uuids?: string[],
  public?: boolean,
  published: boolean,
  score?: number,
  screenshots?: string[],
  slug: string,
  standard_refs?: string[],
  strategy_uuids?: string[],
  subjects: string[],
  thumbnail_url: string,
  title: string,
  topic_uuids?: string[],
  updated_at?: string,
  updated_by?: number,
  uuid: string,
}

export type LibraryObjectsByType = {
  goals: LibraryContentObject[],
  strategies: LibraryContentObject[],
  resources: LibraryContentObject[],
  presentlevels: LibraryContentObject[],
  impact_of_disabilities: LibraryContentObject[]
}

export type ContributorUserInfo = {
  DomainUUID: string
  Email: string
  LicenseEndDate: string
  Name: string
  UserUUID: string
}

export type ContributorRoleChangeResponse = {
  authorized: string[]
  unauthorized: string[]
  success: string[]
  users: FolderUserRole[]
  folder_uuid: string
  owner_uuid: string
  errors: string[]
}

export type FolderUserRole = {
  user_uuid: string
  role: string
  user_email: string
  user_name: string
}

export type FolderUsersResponse = {
  users: FolderUserRole[]
}

export type DeleteResourceResponse = {
  toolkit_deletion: string,
  collections_deletion: boolean
}

export type ViewMode = typeof constants.LIBRARY_MODE_ALL | typeof constants.LIBRARY_MODE_ARCHIVED | typeof constants.LIBRARY_MODE_OWNED | typeof constants.LIBRARY_MODE_SHARED

export type ContributorIdsForContentMap = {
  [contentUuid: string]: {contributor: string, isFolderOwner: boolean}
}

export enum FolderRole {
  OWNER = 'owner',
  CONTRIBUTOR = 'contributor',
  NONE = 'none'
}

export const
  EditDocument = 'edit_document',
  RemoveContent = 'remove_content'